const CONSENT_STORAGE_KEY = 'onedashAnalyticsConsent';
const CONSENT_COOKIE_NAME = 'onedashAnalyticsConsent';
const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const CONSENT_GRANTED = 'granted';
const CONSENT_DENIED = 'denied';
const CAMPAIGN_PARAMETER_NAMES = Object.freeze({
  utm_source: 'campaign_source',
  utm_medium: 'campaign_medium',
  utm_campaign: 'campaign_name',
});

const EVENT_DEFINITIONS = Object.freeze({
  'Onboarding: Account details visited': {
    name: 'tutorial_begin',
    parameters: { tutorial_name: 'account_setup' },
  },
  'Onboarding: Account details completed': {
    name: 'tutorial_complete',
    parameters: { tutorial_name: 'account_setup' },
  },
  'Added an inbox': { name: 'inbox_connected' },
  'Sent a message': { name: 'message_sent' },
  'Sent a private note': { name: 'private_note_sent' },
  'Opened conversation via inbox': {
    name: 'conversation_opened',
    parameters: { entry_point: 'inbox' },
  },
  'Opened conversation via notification': {
    name: 'conversation_opened',
    parameters: { entry_point: 'notification' },
  },
  'Searched conversations': {
    name: 'search',
    parameters: { search_location: 'conversations' },
  },
  'Searched contacts list': {
    name: 'search',
    parameters: { search_location: 'contacts' },
  },
  'Imported contacts successfully': { name: 'contacts_imported' },
  'Import contacts failed': { name: 'contacts_import_failed' },
  'Created a new campaign': { name: 'campaign_created' },
  'Inserted article into reply via article search': {
    name: 'article_inserted_into_reply',
  },
});

// Only known, low-cardinality product metadata may leave OneDash. In particular,
// raw IDs, names, message content, labels, search terms, URLs, and errors are not
// accepted here even if a caller accidentally supplies them.
const SAFE_PARAMETER_NAMES = Object.freeze({
  businessHours: 'business_hours',
  channelType: 'channel_type',
  company_size: 'company_size',
  ctaName: 'cta_name',
  cta_name: 'cta_name',
  editedBeforeSend: 'edited_before_send',
  entryPoint: 'entry_point',
  from: 'source',
  hasArticles: 'has_articles',
  hasDescription: 'has_description',
  hasReplyTo: 'has_reply_to',
  has_enriched_data: 'has_enriched_data',
  industry: 'industry',
  isPrivate: 'is_private',
  method: 'method',
  newLocale: 'new_locale',
  newValue: 'new_value',
  operation: 'operation',
  referral_source: 'referral_source',
  reportType: 'report_type',
  signatureEnabled: 'signature_enabled',
  siteArea: 'site_area',
  site_area: 'site_area',
  totalLocales: 'total_locales',
  uiFrom: 'ui_source',
  user_role: 'user_role',
});

const readConsentCookie = () => {
  const cookie = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith(`${CONSENT_COOKIE_NAME}=`));

  if (!cookie) return null;

  try {
    return decodeURIComponent(cookie.split('=').slice(1).join('='));
  } catch {
    return null;
  }
};

const consentCookieDomain = () => {
  const hostname = window.location.hostname;
  return hostname === 'onedash.me' || hostname.endsWith('.onedash.me')
    ? '.onedash.me'
    : null;
};

const writeConsentCookie = value => {
  const domain = consentCookieDomain();
  const domainAttribute = domain ? `; Domain=${domain}` : '';
  const secureAttribute =
    window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(
    value
  )}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax${domainAttribute}${secureAttribute}`;
};

const readConsent = () => {
  const cookieConsent = readConsentCookie();
  if ([CONSENT_GRANTED, CONSENT_DENIED].includes(cookieConsent)) {
    return cookieConsent;
  }

  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeConsent = value => {
  writeConsentCookie(value);

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Storage can be unavailable in hardened browsers. The current page still
    // respects the choice, but the banner will be shown again next visit.
  }
};

const removeAnalyticsCookies = () => {
  const parentDomain = window.location.hostname.split('.').slice(-2).join('.');
  const domains = ['', window.location.hostname, `.${parentDomain}`];
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (!name.startsWith('_ga')) return;

    domains.forEach(domain => {
      const domainAttribute = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttribute}; SameSite=Lax; Secure`;
    });
  });
};

const safeString = value => value.trim().slice(0, 100);

const campaignParameters = () => {
  const search = new URLSearchParams(window.location.search);
  return Object.entries(CAMPAIGN_PARAMETER_NAMES).reduce(
    (parameters, [queryName, analyticsName]) => {
      const value = search.get(queryName);
      if (value && /^[a-zA-Z0-9_-]{1,100}$/.test(value)) {
        parameters[analyticsName] = value;
      }
      return parameters;
    },
    {}
  );
};

export const appendCampaignParameters = path => {
  const search = new URLSearchParams(window.location.search);
  const campaignSearch = new URLSearchParams();
  Object.keys(CAMPAIGN_PARAMETER_NAMES).forEach(queryName => {
    const value = search.get(queryName);
    if (value && /^[a-zA-Z0-9_-]{1,100}$/.test(value)) {
      campaignSearch.set(queryName, value);
    }
  });

  const query = campaignSearch.toString();
  return query ? `${path}?${query}` : path;
};

export const normalizeEventName = eventName => {
  const mappedName = EVENT_DEFINITIONS[eventName]?.name;
  if (mappedName) return mappedName;

  return String(eventName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
};

export const sanitizeEventParameters = properties =>
  Object.entries(properties || {}).reduce((safeProperties, [key, value]) => {
    const parameterName = SAFE_PARAMETER_NAMES[key];
    if (!parameterName) return safeProperties;

    if (typeof value === 'string') {
      const normalizedValue = safeString(value);
      if (normalizedValue) safeProperties[parameterName] = normalizedValue;
    } else if (typeof value === 'boolean' || typeof value === 'number') {
      safeProperties[parameterName] = value;
    }

    return safeProperties;
  }, {});

/**
 * Privacy-scoped Google Analytics 4 adapter used by existing OneDash events.
 */
export class AnalyticsHelper {
  constructor({ measurementId, host, consentRequired = true } = {}) {
    this.measurementId = measurementId;
    this.host = host;
    this.consentRequired = consentRequired;
    this.analytics = null;
    this.user = null;
    this.previousPageLocation = null;
    this.consent = readConsent();
  }

  isConfigured() {
    return Boolean(
      /^G-[A-Z0-9]+$/.test(this.measurementId || '') &&
        this.host &&
        window.location.hostname === this.host
    );
  }

  requiresConsent() {
    return (
      this.isConfigured() &&
      this.consentRequired &&
      ![CONSENT_GRANTED, CONSENT_DENIED].includes(this.consent)
    );
  }

  consentStatus() {
    return this.consent;
  }

  init() {
    if (!this.isConfigured()) return;

    if (!this.consentRequired || this.consent === CONSENT_GRANTED) {
      this.load();
    }
  }

  load() {
    if (this.analytics || !this.isConfigured()) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };

    this.analytics = window.gtag;
    this.analytics('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'granted',
    });
    this.analytics('consent', 'update', { analytics_storage: 'granted' });
    this.analytics('js', new Date());
    this.analytics('config', this.measurementId, {
      allow_ad_personalization_signals: false,
      allow_google_signals: false,
      ...campaignParameters(),
      send_page_view: false,
    });

    if (
      !document.querySelector(
        `script[data-onedash-ga4="${this.measurementId}"]`
      )
    ) {
      const script = document.createElement('script');
      script.async = true;
      script.dataset.onedashGa4 = this.measurementId;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        this.measurementId
      )}`;
      document.head.appendChild(script);
    }

    if (this.user) this.applyIdentity();
  }

  setConsent(granted) {
    this.consent = granted ? CONSENT_GRANTED : CONSENT_DENIED;
    writeConsent(this.consent);

    if (granted) {
      this.load();
      return;
    }

    if (this.analytics) {
      this.analytics('config', this.measurementId, {
        send_page_view: false,
        user_id: null,
        user_properties: {
          account_role: null,
          account_status: null,
        },
      });
      this.analytics('consent', 'update', { analytics_storage: 'denied' });
    }
    removeAnalyticsCookies();
    this.analytics = null;
  }

  identify(user) {
    if (!user) return;

    this.user = user;
    this.applyIdentity();
  }

  applyIdentity() {
    if (!this.analytics || !this.user?.id) return;

    const currentAccount = (this.user.accounts || []).find(
      account => account.id === this.user.account_id
    );
    const userProperties = {};
    if (currentAccount?.role) userProperties.account_role = currentAccount.role;
    if (currentAccount?.status) {
      userProperties.account_status = currentAccount.status;
    }

    this.analytics('config', this.measurementId, {
      allow_ad_personalization_signals: false,
      allow_google_signals: false,
      send_page_view: false,
      user_id: `user-${this.user.id}`,
      user_properties: userProperties,
    });
  }

  reset() {
    this.user = null;
    if (!this.analytics) return;

    this.analytics('config', this.measurementId, {
      send_page_view: false,
      user_id: null,
      user_properties: {
        account_role: null,
        account_status: null,
      },
    });
  }

  track(eventName, properties = {}) {
    if (!this.analytics) return;

    const definition = EVENT_DEFINITIONS[eventName] || {};
    const parameters = {
      ...(definition.parameters || {}),
      ...sanitizeEventParameters(properties),
      send_to: this.measurementId,
      transport_type: 'beacon',
    };
    this.analytics('event', normalizeEventName(eventName), parameters);
  }

  page(pageName) {
    if (!this.analytics || !pageName) return;

    const safePageName = normalizeEventName(pageName);
    const pageLocation = `${window.location.origin}/app/analytics/${safePageName}`;
    const parameters = {
      page_location: pageLocation,
      page_title: safePageName,
      send_to: this.measurementId,
    };
    if (this.previousPageLocation) {
      parameters.page_referrer = this.previousPageLocation;
    }

    this.analytics('event', 'page_view', parameters);
    this.previousPageLocation = pageLocation;
  }
}

// This object is shared across the dashboard and authentication entrypoints.
export default new AnalyticsHelper(window.analyticsConfig);
